/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Radio,
  Divider,
  useTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import InventoryForm from "./InventoryForm";
import inventorySetupStyles from "./InventorySetup.styles";
import { BUTTON_LABELS } from "../../constants/lang";
import editIcon from "../../assets/images/photo-icon.svg";
import deleteIcon from "../../assets/images/delete-icon.svg";
import { fetchProducts } from "../../store/reducers/createProfile";
import { ToastNotifyError, ToastNotifySuccess } from "../../components/CommonComponents/Toast/ToastNotify";
import { fetchDeleteProduct, fetchGetProductById, fetchSellerProfile, fetchShopifyList } from "../../store/reducers/createProfile/apithunk";
import { updateLoading } from "../../store/reducers/loader";
import rollbar from "../../rollbarConfig";
import infoIcon from "../../assets/images/infoIcon.svg";
import shopifyLogo from "../../assets/images/shopifyLogo.svg";
import CreateSopify from "../CreateProfile/CreateSopify";
import { ROUTE_PATH } from "../../constants/route";
import wanderobelogo from "../../assets/images/wanderobe-logo-1.svg";

const InventorySetup = ({
  control,
  errors,
  onValueChange,
  products,
  resetShowAddProduct,
  onShowAddProductChange,
  setValue,
  trigger,
  watch,
  clearErrors,
  setError,
  OnEditAndDelete,
  onEditSubmit,
  SelectedProductId,
  onShowEditProductChange,
  reset,
  defaultInventoryType,
  showEditProductFromParent
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = inventorySetupStyles(theme);
  const [activeStates, setActiveStates] = useState({});
  const [checkProducts, setCheckProducts] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [editData, setEditData] = useState([]);
  const [openMenus, setOpenMenus] = useState({});
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const observer = useRef(null);
  const [editForm, setEditForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  
  // Set default option based on prop
  useEffect(() => {
    if (defaultInventoryType) {
      setSelectedOption(defaultInventoryType);
    }
  }, [defaultInventoryType]);

  // Check for mapped products and redirect to dashboard if any exist
  const checkForMappedProducts = async () => {
    try {
      const query = `?page=1&page_size=10`;
      const res = await dispatch(fetchShopifyList(query));
      const { data, success } = res?.payload || {};

      if (success && data?.products) {
        const hasMappedProducts = data?.products?.some(product => product?.is_mapped === true);
        if (hasMappedProducts) {
          navigate(ROUTE_PATH.PRIVATE_DASHBOARD);
          return true;
        }
      }
    } catch (error) {
      console.error("Failed to check for mapped products:", error);
    }
    return false;
  };

  // Check for mapped products on component mount and when selected option changes
  useEffect(() => {
    checkForMappedProducts();
  }, []);

  const handleOptionChange = (event) => {
    const newValue = event.target.value;
    setSelectedOption(newValue);

    // Update URL based on selection
    const urlParams = new URLSearchParams(location?.search);

    if (newValue === 'shopify') {
      urlParams.set('inventory-type', 'shopify');
    } else if (newValue === 'manually') {
      urlParams.set('inventory-type', 'manually');
    } else {
      urlParams.delete('inventory-type');
    }

    const newSearch = urlParams.toString();
    const newPath = newSearch ? `${location?.pathname}?${newSearch}` : location?.pathname;
    navigate(newPath, { replace: true });
  };

  const handleToggle = (productId) => {
    setActiveStates((prev) => ({
      ...prev,
      [productId]: !prev?.[productId],
    }));
  };
  
  const handleToggleEditDelete = (event, productId) => {
    event.stopPropagation();
    setOpenMenus((prev) => ({
      ...prev,
      [productId]: prev?.[productId] ? null : event.currentTarget,
    }));
  };

  const handleCloseMenu = (productId) => {
    setOpenMenus((prev) => ({
      ...prev,
      [productId]: null,
    }));
  };
  
  useEffect(() => {
    setShowAddProduct(false);
  }, [resetShowAddProduct]);

  useEffect(() => {
    setCheckProducts(products);
  }, [products]);

  const handleDeleteProduct = async(id) => {
    try {
      const res = await dispatch(fetchDeleteProduct(id));
      const { status, message } = res?.payload;
      if (status) {
        handleCloseMenu(id);
        ToastNotifySuccess(message);
        OnEditAndDelete?.();
      } else {
        ToastNotifyError(message);
      }
    } catch (error) {
      rollbar.error("Error message", error);
      console.error("Login failed:", error);
    }
  };

  const getAllProducts = async () => {
    dispatch(updateLoading(true));
    try {
      const SellerRes = await dispatch(fetchSellerProfile());
      const { data: { id } } = SellerRes.payload;
      const query = `?search=&category=All&page_no=${page}&page_size=10&seller=${id}`;
      const res = await dispatch(fetchProducts(query));
      const { data, status, message, _meta: { total_count } = {} } = res.payload;
      if (status) {
        setTotalCount(total_count)
        setCheckProducts((prev) => {
          const existingIds = new Set(prev?.map((product) => product?.id));
          const newProducts = Array.isArray(data) ? data?.filter((product) => !existingIds?.has(product?.id)) : [];
          return [...prev, ...newProducts];
        });
      } else {
        ToastNotifyError(message);
      }
    } catch {
      rollbar.error("Error message");
      console.error("API error: ", "Error");
    } finally {
      dispatch(updateLoading(false))
    }
  };

  useEffect(() => {
    getAllProducts();
  }, [page]);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries?.[0]?.isIntersecting && checkProducts?.length < totalCount) {
        setPage((prev) => prev + 1);
      }
    });

    if (document?.querySelector("#last-row")) {
      observer.current.observe(document.querySelector("#last-row"));
    }
  }, [checkProducts]);

  useEffect(() => {
    if (onShowAddProductChange) {
      onShowAddProductChange(showAddProduct);
    }
  }, [showAddProduct]);

  useEffect(() => {
    if (onShowEditProductChange) {
      onShowEditProductChange(showEditProduct);
    }
  }, [showEditProduct]);

  // Sync local state with parent prop
  useEffect(() => {
    if (showEditProductFromParent !== undefined) {
      setShowEditProduct(showEditProductFromParent);
    }
  }, [showEditProductFromParent]);

  const handleClose = () => {
    setShowAddProduct(false);
    setShowEditProduct(false);
    setEditData([]);
    SelectedProductId(null);
    setEditForm(false);
    reset();
  };

  useEffect(() => {
    if (checkProducts?.length > 0) {
      const initialStates = checkProducts?.reduce((acc, product) => {
        acc[product?.id] = product?.status === "In";
        return acc;
      }, {});
      setActiveStates(initialStates);
    }
  }, [checkProducts]);

  const handleShowEditProduct = async(id) => {
    setShowAddProduct(false);
    try {
      const res = await dispatch(fetchGetProductById(id));
      const { data, status, message } = res?.payload;
      if (status) {
        setOpenMenus((prev) => ({
          ...prev,
          [id]: false
        }));
        setEditData(data);
        setEditForm(true);
        setShowEditProduct(true);

      } else {
        ToastNotifyError(message);
      }
    } catch (error) {
      rollbar.error("Error message", error);
      console.error("Login failed:", error);
    }
  };

  return (
    <Box>
      <Box sx={styles.header}>
        <Typography sx={styles.inventorySetupHeader}>Inventory Setup</Typography>
        <Typography sx={styles.inventorySetupSubHeader}>
          Effortlessly set up your store and organize your inventory.
        </Typography>
      </Box>
      <Box>
        <Box sx={{ display: 'flex', gap: 6, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Radio
              sx={{
                mr: '8px',
                p: 0,
                color: '#BFBFBF',
                '&.Mui-checked': {
                  color: '#1243B5',
                },
                '& .MuiSvgIcon-root': {
                  width: 26,
                  height: 26,
                },
              }}
              value="manually"
              name="import-type"
              id="manually"
              checked={selectedOption === 'manually'}
              onChange={handleOptionChange}
            />
            <Typography component="label" htmlFor="manually" sx={{ fontFamily: 'Lexend', fontSize: 24, fontWeight: 700, lineHeight: '24px', color: '#0F0F0F' }}>Manually</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Radio
              sx={{
                mr: '8px',
                p: 0,
                color: '#BFBFBF',
                '&.Mui-checked': {
                  color: '#1243B5',
                },
                '& .MuiSvgIcon-root': {
                  width: 26,
                  height: 26,
                },
              }}
              value="shopify"
              name="import-type"
              id="shopify"
              checked={selectedOption === 'shopify'}
              onChange={handleOptionChange}
            />
            <Typography component="label" htmlFor="shopify" sx={{ fontFamily: 'Lexend', fontSize: 24, fontWeight: 700, lineHeight: '24px', color: '#0F0F0F' }}>Shopify</Typography>
            <Box component={"img"} src={shopifyLogo} sx={{ width: 26, height: 30, ml: '6.61px' }} />
          </Box>
        </Box>
        {selectedOption === 'shopify' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box component={"img"} src={infoIcon} sx={{ width: 18, height: 18 }} />
            <Typography sx={{ width: '600px', fontFamily: 'Lexend', fontSize: 16, fontWeight: 300, lineHeight: '24px', color: '#656565' }}>
              Connect your Shopify store to import items automatically, or enter product details manually.
            </Typography>
          </Box>
        )}
        <Divider sx={{ mb: "20px" }} />
      </Box>

      {selectedOption === 'manually' && (
        checkProducts?.length > 0 ? (
          <Box>
            <Table sx={styles.webTable}>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.tableCell}>PRODUCT NAME</TableCell>
                  <TableCell sx={styles.tableCell}>TOTAL QTY</TableCell>
                  <TableCell sx={styles.tableCell}>VARIANTS</TableCell>
                  <TableCell sx={styles.tableCell}>STATUS</TableCell>
                  <TableCell sx={{ ...styles.tableCell, ...styles.textAlign }}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checkProducts?.map((element, index) => {
                  const isLastRow = index === checkProducts?.length - 1;
                  return (
                    <TableRow key={element.id} id={isLastRow ? "last-row" : undefined}>
                      <TableCell>
                        <Box sx={styles.productCell}>
                          <Box sx={styles.productImageWrapCell}>
                            <Box
                              component="img"
                              src={element?.variants?.[0]?.variant_img?.[0] || element?.images?.[0] || wanderobelogo}
                              alt='dp'
                              sx={styles.boxImg}
                            />
                          </Box>
                          <Box sx={styles.productName}>
                            {element?.name?.length > 20 ? `${element.name.substring(0, 20)}...` : element.name}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{element?.quantity}</TableCell>
                      <TableCell>{element?.variants?.length}</TableCell>
                      <TableCell sx={{ minWidth: "115px" }}>
                        <Box sx={styles.switchWrap}>
                          <Switch
                            sx={styles.customSwitch(activeStates[element.id])}
                            checked={activeStates[element.id] || false}
                            onChange={() => handleToggle(element.id)}
                          />
                          <Box sx={styles.customSwitchText}>
                            {activeStates[element.id] ? "Active" : "Inactive"}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ ...styles.moreIcon, ...styles.textAlign }}>
                          <IconButton
                            onClick={(e) => handleToggleEditDelete(e, element.id)}
                            size="small"
                            sx={{ padding: '4px' }}
                          >
                            <MoreVertRoundedIcon />
                          </IconButton>
                          <Menu
                            anchorEl={openMenus[element.id]}
                            open={Boolean(openMenus[element.id])}
                            onClose={() => handleCloseMenu(element.id)}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                          >
                            <MenuItem 
                              onClick={() => {
                                handleShowEditProduct(element.id);
                                handleCloseMenu(element.id);
                              }}
                            >
                              <Box component="img" src={editIcon} alt='editIcon' sx={{ mr: 1, width: '16px', height: '16px' }} />
                              <Typography sx={styles.moreText}>Edit</Typography>
                            </MenuItem>
                            <MenuItem 
                              onClick={() => {
                                handleDeleteProduct(element.id);
                                handleCloseMenu(element.id);
                              }}
                            >
                              <Box component="img" src={deleteIcon} alt='deleteIcon' sx={{ mr: 1, width: '16px', height: '16px' }} />
                              <Typography sx={{ ...styles.moreText, ...styles.removeText }}>Remove</Typography>
                            </MenuItem>
                          </Menu>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={styles.showMbTable}>
              {checkProducts?.map((element) => (
                <Box key={element.id} sx={styles.mbTable}>
                  <Box sx={styles.mbTablePart1}>
                    <Box sx={styles.productCell}>
                      <Box sx={styles.productImageWrapCell}>
                        <Box
                          component="img"
                          src={element?.variants?.[0]?.variant_img?.[0] || element?.images?.[0] || wanderobelogo}
                          alt='box-img'
                          sx={styles.boxImg}
                        />
                      </Box>
                      {element?.name?.length > 3 ? `${element.name.substring(0, 3)}...` : element.name}
                    </Box>
                    <Box sx={styles.mbSwitchMore}>
                      <Box sx={styles.switchWrap}>
                        <Switch
                          sx={styles.customSwitch(activeStates[element.id])}
                          checked={activeStates[element.id] || false}
                          onChange={() => handleToggle(element.id)}
                        />
                      </Box>
                      <Box sx={{ ...styles.moreIcon, ...styles.textAlign }}>
                        <IconButton
                          onClick={(e) => handleToggleEditDelete(e, element.id)}
                          size="small"
                          sx={{ padding: '4px' }}
                        >
                          <MoreVertRoundedIcon />
                        </IconButton>
                        <Menu
                          anchorEl={openMenus[element.id]}
                          open={Boolean(openMenus[element.id])}
                          onClose={() => handleCloseMenu(element.id)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem 
                            onClick={() => {
                              handleShowEditProduct(element.id);
                              handleCloseMenu(element.id);
                            }}
                          >
                            <Box component="img" src={editIcon} alt='edit-icon' sx={{ mr: 1, width: '16px', height: '16px' }} />
                            <Typography sx={styles.moreText}>Edit</Typography>
                          </MenuItem>
                          <MenuItem 
                            onClick={() => {
                              handleDeleteProduct(element.id);
                              handleCloseMenu(element.id);
                            }}
                          >
                            <Box component="img" src={deleteIcon} alt='delete-icon' sx={{ mr: 1, width: '16px', height: '16px' }} />
                            <Typography sx={{ ...styles.moreText, ...styles.removeText }}>Remove</Typography>
                          </MenuItem>
                        </Menu>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={styles.mbTablePart2}>
                    <Box>
                      <Typography sx={styles.mbTableHead}>
                        Total qty.
                      </Typography>
                      <Typography sx={styles.mbTableBody}>
                        {element.quantity}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={styles.mbTableHead}>Sold qty.</Typography>
                      <Typography sx={styles.mbTableBody}>--</Typography>
                    </Box>
                    <Box>
                      <Typography sx={styles.mbTableHead}>
                        Available qty.
                      </Typography>
                      <Typography sx={styles.mbTableBody}>--</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            {showAddProduct && (
              <Box sx={styles.addFormWrap}>
                <InventoryForm
                  control={control}
                  errors={errors}
                  onValueChange={onValueChange}
                  updateForm={true}
                  onClose={handleClose}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
                  clearErrors={clearErrors}
                  setError={setError}
                  editForm={false}
                  OnEditAndDelete={OnEditAndDelete}
                  handleShowEditProduct={handleShowEditProduct}
                />
              </Box>
            )}
            {showEditProduct && (
              <Box sx={styles.addFormWrap}>
                <InventoryForm
                  control={control}
                  errors={errors}
                  onValueChange={onValueChange}
                  editForm={editForm}
                  EditData={editData}
                  updateForm={true}
                  setError={setError}
                  onClose={handleClose}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
                  clearErrors={clearErrors}
                  OnDeleteProduct={handleDeleteProduct}
                  onEditSubmit={onEditSubmit}
                  SelectedProductId={SelectedProductId}
                  OnEditAndDelete={OnEditAndDelete}
                  handleShowEditProduct={handleShowEditProduct}
                />
              </Box>
            )}
            {!showAddProduct && !showEditProduct && (
              <Button
                type='submit'
                variant='outlined'
                color='primary'
                aria-label='add-btn'
                sx={styles.addBtn}
                onClick={() => setShowAddProduct(true)}
              >
                {BUTTON_LABELS.ADD_NEW_PRODUCT}
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <InventoryForm
              control={control}
              errors={errors}
              onValueChange={onValueChange}
              watch={watch}
              setValue={setValue}
              trigger={trigger}
              clearErrors={clearErrors}
              OnEditAndDelete={OnEditAndDelete}
              handleShowEditProduct={handleShowEditProduct}
            />
          </Box>
        )
      )}

      {selectedOption === 'shopify' && (
        <CreateSopify />
      )}
    </Box>
  );
};

export default InventorySetup;

